import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Sparkles, ArrowRight, Clock, BookOpen, Brain } from "lucide-react";

const SSC_COLORS = [
  "#1E3A8A", "#F59E0B", "#E11D48", "#10B981", "#0EA5E9",
  "#8B5CF6", "#F43F5E", "#0D9488", "#7C3AED", "#EA580C",
];

function colorForSsc(code) {
  let hash = 0;
  for (let i = 0; i < code.length; i++) hash = (hash * 31 + code.charCodeAt(i)) >>> 0;
  return SSC_COLORS[hash % SSC_COLORS.length];
}

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [sscs, setSscs] = useState([]);
  const [activeSsc, setActiveSsc] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/courses").then(({ data }) => setCourses(data));
    api.get("/courses/sscs").then(({ data }) => setSscs(data));
  }, []);

  const filtered = useMemo(() => {
    return courses.filter((c) => {
      if (activeSsc !== "all" && c.ssc_code !== activeSsc) return false;
      if (q) {
        const s = q.toLowerCase();
        return (
          c.course_name.toLowerCase().includes(s) ||
          c.ssc_name.toLowerCase().includes(s) ||
          c.ai_integration.toLowerCase().includes(s)
        );
      }
      return true;
    });
  }, [courses, activeSsc, q]);

  return (
    <div className="min-h-screen bg-paper">
      <div className="h-1.5 gradient-stripe" />
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1E3A8A] flex items-center justify-center text-white font-heading font-bold">PS</div>
            <div>
              <div className="font-heading font-bold text-xl">padaskillinfo</div>
              <div className="text-xs text-gray-500 -mt-0.5">AI Upskill · NSDC SSC Catalog</div>
            </div>
          </Link>
          <Link to="/register"><Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-white rounded-full">Register Now <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-10 animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-[#FEF3C7] text-[#92400E] px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Every NSDC course offered as AI Upskill variant
          </div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            <span className="text-[#1E3A8A]">{courses.length}</span> AI-integrated <br className="hidden sm:block" />
            <span className="text-[#E11D48]">Sector Skill Council</span> courses
          </h1>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            From Agriculture to Telecom, every course we offer integrates artificial intelligence
            relevant to that sector — designed for rural youth at the RTIH Pithapuram incubation centre.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-4 top-3.5 text-gray-400" />
            <Input
              placeholder="Search courses, sectors, or AI skills…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-11 h-11 bg-white"
              data-testid="courses-search"
            />
          </div>
        </div>

        {/* SSC chips */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          <button
            onClick={() => setActiveSsc("all")}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
              activeSsc === "all" ? "bg-[#1E3A8A] text-white" : "bg-white text-gray-700 border border-gray-200 hover:border-gray-300"
            }`}
            data-testid="ssc-filter-all"
          >
            All ({courses.length})
          </button>
          {sscs.map((s) => (
            <button
              key={s.code}
              onClick={() => setActiveSsc(s.code)}
              style={{
                backgroundColor: activeSsc === s.code ? colorForSsc(s.code) : undefined,
                color: activeSsc === s.code ? "white" : undefined,
                borderColor: activeSsc === s.code ? colorForSsc(s.code) : undefined,
              }}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all border ${
                activeSsc === s.code ? "" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`ssc-filter-${s.code}`}
            >
              {s.code} ({s.course_count})
            </button>
          ))}
        </div>

        {/* Course grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((c) => (
            <article
              key={c.course_id}
              className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 flex flex-col"
              data-testid={`course-${c.course_code}`}
            >
              <div className="flex items-start justify-between mb-3">
                <Badge style={{ backgroundColor: colorForSsc(c.ssc_code) }} className="text-white hover:opacity-90 border-0">
                  {c.ssc_code}
                </Badge>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[#FEF3C7] to-[#FFE4E6] border border-[#FCD34D]/40 text-[#92400E] text-[10px] font-bold uppercase tracking-wider">
                  <Sparkles className="w-3 h-3" /> AI Upskill
                </div>
              </div>

              <h3 className="font-heading font-bold text-lg leading-tight mb-1">{c.course_name}</h3>
              <div className="text-xs text-gray-500 mb-3">{c.ssc_name}</div>

              <div className="flex items-center gap-3 text-xs text-gray-600 mb-4">
                <span className="inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> NSQF L{c.nsqf_level}</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {c.duration_weeks} weeks</span>
                <span className="font-mono text-gray-400">{c.course_code}</span>
              </div>

              <div className="bg-gradient-to-br from-[#1E3A8A]/5 to-[#E11D48]/5 rounded-lg p-3 mb-3 border border-gray-100">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#1E3A8A] font-bold mb-1">
                  <Brain className="w-3 h-3" /> AI Integration
                </div>
                <p className="text-sm text-gray-700 leading-snug">{c.ai_integration}</p>
              </div>

              <div className="mt-auto">
                <Link to="/register">
                  <Button variant="outline" size="sm" className="w-full group-hover:border-[#1E3A8A] group-hover:text-[#1E3A8A]">
                    Express interest <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </div>
            </article>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center text-gray-500 py-20">No courses match your filter.</div>
        )}
      </section>

      <footer className="bg-[#1E3A8A] text-white mt-10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-white/80">
            All courses aligned with NSDC Sector Skill Councils · AI Upskill overlays added by RTIH Pithapuram
          </div>
          <Link to="/register"><Button className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 rounded-full">Register Now</Button></Link>
        </div>
      </footer>
    </div>
  );
}
